import { useEffect, useRef, useState } from 'react';
import { Navigation, ClipboardCheck, Pencil, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { inspectWaterSource, getWaterSource } from '@/api/waterSources';
import { useAuthedObjectUrl } from '@/hooks/useAuthedObjectUrl';
import { useAuth } from '@/contexts/AuthContext';
import { ShareSheet } from '@/components/common/ShareSheet';
import { directionsUrl, fetchShareFile, openDirections, type SharePayload } from '@/utils/share';

export type SourceType = 'HYDRANT' | 'PRIVATE';
export type SourceCondition = 'GOOD' | 'FAIR' | 'POOR' | 'OUT_OF_SERVICE';

export interface WaterSourceRecord {
  id: number;
  name: string;
  source_type: SourceType;
  condition: SourceCondition;
  lat: number;
  lng: number;
  address?: string | null;
  capacity_litres?: number | null;
  flow_rate_lpm?: number | null;
  access_notes?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  fpa_member_id?: number | null;
  last_inspected?: string | null;
  notes?: string | null;
  photo_path?: string | null;
}

interface WaterSourceDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: WaterSourceRecord | null;
  canEdit?: boolean;
  onEdit?: (source: WaterSourceRecord) => void;
  onInspected?: (updated: WaterSourceRecord) => void;
}

const CONDITION_LABEL: Record<SourceCondition, string> = {
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  OUT_OF_SERVICE: 'Out of service',
};

const CONDITION_COLOR: Record<SourceCondition, string> = {
  GOOD: 'bg-green-100 text-green-800 border-green-300',
  FAIR: 'bg-amber-100 text-amber-800 border-amber-300',
  POOR: 'bg-red-100 text-red-800 border-red-300',
  OUT_OF_SERVICE: 'bg-gray-100 text-gray-600 border-gray-300',
};

export function WaterSourceDetailDrawer({
  open,
  onOpenChange,
  source,
  canEdit,
  onEdit,
  onInspected,
}: WaterSourceDetailDrawerProps) {
  const { can } = useAuth();
  const canInspect = can('water_source', 'manage');
  const [expanded, setExpanded] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [inspectCondition, setInspectCondition] = useState<SourceCondition>('GOOD');
  const [inspectNotes, setInspectNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  /**
   * Detail fields fetched when the drawer opens.
   *
   * The map overlay hands us a `WaterSourceMapItem` — id, name, type,
   * condition, lat/lng, address, photo_path, and nothing else. Rendering that
   * straight through is why tapping a source on the map showed a drawer with
   * every useful row blank (capacity, flow rate, access notes, contacts)
   * while tapping the same source in the table showed everything.
   *
   * Hydrating here rather than in the overlay covers all three entry points:
   * the overlay's own drawer, a parent-owned `onSourceClick`, and the table.
   */
  const [hydrated, setHydrated] = useState<WaterSourceRecord | null>(null);
  const [hydrating, setHydrating] = useState(false);

  /**
   * Photo pre-resolved into a File so it can ride along on the native share.
   * Fetching it inside the click handler would spend the transient user
   * activation `navigator.share` requires, which iOS Safari then rejects.
   */
  const shareFileRef = useRef<File | null>(null);

  const sourceId = source?.id ?? null;
  // Merge order matters: the fetched record wins, but only for the source
  // actually on screen — a stale `hydrated` from the previously tapped pin
  // must never paint over the new one.
  const detail: WaterSourceRecord | null =
    source && hydrated?.id === source.id ? { ...source, ...hydrated } : source;

  useEffect(() => {
    if (open) {
      setExpanded(false);
      setInspecting(false);
      setInspectNotes('');
      if (source) setInspectCondition(source.condition);
    }
  }, [open, source]);

  useEffect(() => {
    if (!open || sourceId == null) return undefined;
    let cancelled = false;
    setHydrating(true);
    getWaterSource(sourceId)
      .then((full: WaterSourceRecord) => {
        if (!cancelled) setHydrated(full);
      })
      .catch(() => {
        // Non-fatal: name, type, condition and address still render from the
        // lightweight record we were handed.
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, sourceId]);

  const photoPath = detail?.photo_path ?? null;
  const photoName = detail?.name ?? 'water-source';
  // The photo is behind the JWT authorizer, so a plain <img src> 401s and
  // renders as a broken image. Blob-load it with the token attached.
  const { url: photoUrl } = useAuthedObjectUrl('image', photoPath, open);
  useEffect(() => {
    shareFileRef.current = null;
    if (!open || !photoPath) return undefined;
    let cancelled = false;
    fetchShareFile(`/images/file/${photoPath}`, photoName).then((f) => {
      if (!cancelled) shareFileRef.current = f;
    });
    return () => {
      cancelled = true;
    };
  }, [open, photoPath, photoName]);

  if (!detail) return null;

  // Local alias so the callbacks below narrow cleanly — `source` is a prop,
  // which TypeScript won't keep narrowed inside a closure.
  const record = detail;
  const typeLabel =
    record.source_type === 'HYDRANT' ? 'Hydrant (municipal)' : 'Private (tank/dam/pool)';

  async function handleSaveInspection() {
    setSaving(true);
    try {
      const updated = await inspectWaterSource(record.id, {
        condition: inspectCondition,
        notes: inspectNotes || undefined,
      });
      setHydrated((prev) => (prev ? { ...prev, ...updated } : updated));
      onInspected?.(updated);
      setInspecting(false);
    } finally {
      setSaving(false);
    }
  }

  const sharePayload: SharePayload = {
    title: record.name,
    kind: 'Water source',
    headline: record.name,
    details: [
      ['Type', typeLabel],
      ['Condition', CONDITION_LABEL[record.condition] ?? record.condition],
      ['Address', record.address],
      ['Capacity', record.capacity_litres ? `${record.capacity_litres.toLocaleString()} L` : null],
      ['Flow rate', record.flow_rate_lpm ? `${record.flow_rate_lpm.toLocaleString()} L/min` : null],
      ['Last inspected', record.last_inspected],
      ['Access', record.access_notes],
      // buildShareText no longer inserts this implicitly — each share places
      // it where it reads best. Here that's after the physical details.
      ['Directions', directionsUrl(record.lat, record.lng)],
    ],
    location: { lat: record.lat, lng: record.lng, label: record.name },
    // Water sources keep the email. Unlike the member share, the point here is
    // reaching whoever controls the reservoir by whatever route works.
    contacts:
      record.contact_name || record.contact_phone
        ? [{ name: record.contact_name, phone: record.contact_phone }]
        : [],
    extraLines: record.contact_email ? [`Email: ${record.contact_email}`] : [],
    file: shareFileRef.current,
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl px-4 pb-8">
          <SheetHeader className="mb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <SheetTitle className="truncate text-base">{record.name}</SheetTitle>
                <SheetDescription className="sr-only">Water source details</SheetDescription>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-xs font-normal">{typeLabel}</Badge>
                  <Badge
                    variant="outline"
                    className={`text-xs font-normal ${CONDITION_COLOR[record.condition]}`}
                  >
                    {CONDITION_LABEL[record.condition]}
                  </Badge>
                </div>
              </div>
            </div>
          </SheetHeader>

          {/* Key stats */}
          <div className="space-y-1.5 text-sm">
            {record.address && (
              <div className="flex gap-2">
                <span className="w-24 shrink-0 text-muted-foreground">Address</span>
                <span>{record.address}</span>
              </div>
            )}
            {record.capacity_litres != null && (
              <div className="flex gap-2">
                <span className="w-24 shrink-0 text-muted-foreground">Capacity</span>
                <span>{record.capacity_litres.toLocaleString()} L</span>
              </div>
            )}
            {record.flow_rate_lpm != null && (
              <div className="flex gap-2">
                <span className="w-24 shrink-0 text-muted-foreground">Flow rate</span>
                <span>{record.flow_rate_lpm.toLocaleString()} L/min</span>
              </div>
            )}
            {record.last_inspected && (
              <div className="flex gap-2">
                <span className="w-24 shrink-0 text-muted-foreground">Inspected</span>
                <span>{record.last_inspected}</span>
              </div>
            )}
            {hydrating && (
              <div className="space-y-1.5 pt-0.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
            )}
          </div>

          {/* Expand toggle */}
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="mt-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expanded ? 'Less details' : 'More details'}
          </button>

          {expanded && (
            <div className="mt-3 space-y-2 text-sm">
              {hydrating && !record.access_notes && !record.contact_name && (
                <Skeleton className="h-4 w-48" />
              )}
              {record.access_notes && (
                <div className="flex gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">Access</span>
                  <span className="break-words">{record.access_notes}</span>
                </div>
              )}
              {record.contact_name && (
                <div className="flex gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">Contact</span>
                  <span>{record.contact_name}</span>
                </div>
              )}
              {record.contact_phone && (
                <div className="flex gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">Phone</span>
                  <a href={`tel:${record.contact_phone}`} className="text-primary underline-offset-2 hover:underline">
                    {record.contact_phone}
                  </a>
                </div>
              )}
              {record.contact_email && (
                <div className="flex gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">Email</span>
                  <a href={`mailto:${record.contact_email}`} className="text-primary underline-offset-2 hover:underline truncate">
                    {record.contact_email}
                  </a>
                </div>
              )}
              {record.notes && (
                <div className="flex gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">Notes</span>
                  <span className="break-words">{record.notes}</span>
                </div>
              )}
            </div>
          )}

          {/* Photo */}
          {photoUrl && (
            <img
              src={photoUrl}
              alt={`Photo of ${record.name}`}
              className="mt-2 w-full rounded-lg object-cover max-h-52 border border-border"
            />
          )}

          {/* Inspect inline form */}
          {inspecting && (
            <div className="mt-4 space-y-3 rounded-lg border border-border bg-muted/40 p-3">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Log inspection
              </Label>
              <Select value={inspectCondition} onValueChange={(v) => setInspectCondition(v as SourceCondition)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GOOD">Good</SelectItem>
                  <SelectItem value="FAIR">Fair</SelectItem>
                  <SelectItem value="POOR">Poor</SelectItem>
                  <SelectItem value="OUT_OF_SERVICE">Out of service</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Optional notes…"
                value={inspectNotes}
                onChange={(e) => setInspectNotes(e.target.value)}
                className="h-20 resize-none text-sm"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveInspection} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setInspecting(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => openDirections(record.lat, record.lng, record.name)}
              className="gap-1.5"
            >
              <Navigation className="h-3.5 w-3.5" />
              Get directions
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShareOpen(true)}
              className="gap-1.5"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share
            </Button>
            {/* POST /water-sources/{id}/inspect requires water_source_manage,
                which only the admin tiers hold — STAFF, DATA_LOGGER and FPA_USER
                all reach this drawer with list/read only, and would fill the form
                in just to get a 403 on save. */}
            {!inspecting && canInspect && (
              <Button size="sm" variant="outline" onClick={() => setInspecting(true)} className="gap-1.5">
                <ClipboardCheck className="h-3.5 w-3.5" />
                Log inspection
              </Button>
            )}
            {canEdit && onEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(record)} className="gap-1.5">
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        payload={sharePayload}
        loading={hydrating}
      />
    </>
  );
}
